import { Stack, Paper, Typography, CircularProgress } from "@mui/material";
import { Chart } from "react-chartjs-2";
import { ReportDataModel, ReportModel } from "../../models/report.model";
import {
  ChartDataSourceEnum,
  ChartTickesFormatEnum,
  ChartTypeEnum,
  Chart as ChartDataModel,
} from "../../models/company.model";
import { chartHeight, parseChartExpression, useGroupBy } from "../../chart-helpers/group.table.helper";

export default function CustomizedMixed(props: { chart: ChartDataModel; reports?: ReportDataModel }) {
  const { groupBy } = props.chart || {};
  return (
    <>
      {!groupBy && <CommonMixed chart={props.chart} reports={props.reports} />}
      {groupBy && <GroupByMixed chart={props.chart} reports={props.reports} />}
    </>
  );
}

function GroupByMixed(props: { chart: ChartDataModel; reports?: ReportDataModel }) {
  const { loading, groups, groupLabels } = useGroupBy({ ...props, isSingle: true });
  return (
    <CommonMixed
      chart={props.chart}
      reports={{
        main: {} as ReportModel,
        ranges: groupLabels,
        dates: groups[0].map((r) => ({
          ...r,
          sk: r.groupLabel || "",
        })) as ReportModel[],
      }}
      loading={loading}
    />
  );
}

function CommonMixed(props: { chart: ChartDataModel; reports?: ReportDataModel; loading?: boolean }) {
  const { label, data, mixed } = props.chart || {};
  const dataSource = ChartDataSourceEnum.DateRange;
  const chartLabels = props.reports?.ranges.map((d) => d) || [];
  const datasets = (data || [])
    .map((d) => {
      if (d.mixedType === ChartTypeEnum.Line) {
        return {
          type: ChartTypeEnum.Line,
          label: d.label,
          data: (chartLabels || []).map((label) =>
            parseChartExpression(
              props.reports?.[dataSource]?.find((d) => d.sk === label),
              d.expression,
              d.decimalPlaces
            )
          ),
          backgroundColor: d.color,
          borderColor: d.color,
        };
      } else {
        return {
          type: ChartTypeEnum.Bar,
          label: d.label,
          data: (chartLabels || []).map((label) =>
            parseChartExpression(
              props.reports?.[dataSource]?.find((d) => d.sk === label),
              d.expression,
              d.decimalPlaces
            )
          ),
          backgroundColor: d.color,
          borderRadius: 5,
        };
      }
    })
    .sort((a, b) => (a.type === b.type ? 0 : a.type === ChartTypeEnum.Bar ? 1 : -1));
  const tickesFormat = mixed?.tickesFormat || ChartTickesFormatEnum.Decimal;
  const formatMax =
    tickesFormat === ChartTickesFormatEnum.Percent ? 1 : Math.max(...(datasets || []).map((d) => d.data).flat()) + 5;

  return (
    <Stack spacing={2}>
      <Paper elevation={2} className="padding-small">
        <Typography variant={"h6"} textAlign={"center"}>
          {label || "\u00A0"}
        </Typography>
      </Paper>
      <Paper elevation={2} className="padding-normal">
        <div style={{ height: chartHeight }}>
          {!props.loading && (
            <Chart
              plugins={[
                {
                  id: "overBar",
                  afterDatasetsDraw: (context) => {
                    var ctx = context.ctx;
                    context.data.datasets.forEach(function (dataset, datasetIndex) {
                      if (dataset.type !== ChartTypeEnum.Bar) {
                        return;
                      }
                      const meta = context.getDatasetMeta(datasetIndex);
                      const decimalPlaces = data[datasetIndex]?.decimalPlaces || 0
                      meta.data.forEach((item, i) => {
                        const text = (() => {
                          const value = dataset.data[i] as number
                          if (!value) {
                            return "";
                          }
                          if (tickesFormat === ChartTickesFormatEnum.Percent) {
                            return `${(value * 100).toFixed(decimalPlaces)}%`;
                          }
                          if (tickesFormat === ChartTickesFormatEnum.Currency) {
                            return `$${value.toLocaleString("en-US", {
                              minimumFractionDigits: decimalPlaces || 2,
                              maximumFractionDigits: decimalPlaces || 2,
                            })}`;
                          }
                          return value.toFixed(decimalPlaces);
                        })();
                        ctx.textAlign = "center";
                        ctx.textBaseline = "middle";
                        ctx.fillStyle = "grey";
                        ctx.save();
                        ctx.translate(item.x, item.y - 10);
                        ctx.fillText(String(text), 0, 0);
                        ctx.restore();
                      });
                    });
                  },
                },
              ]}
              type="bar"
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "right",
                    labels: {
                      padding: 20,
                      boxWidth: 15,
                      usePointStyle: true,
                      pointStyle: "circle",
                    },
                  },
                  tooltip: {
                    callbacks: {
                      label(tooltipItem) {
                        const decimalPlaces = data[tooltipItem.datasetIndex]?.decimalPlaces || 0;
                        const formattedValue =
                          typeof tooltipItem.raw === "number"
                            ? tooltipItem.raw.toFixed(decimalPlaces)
                            : tooltipItem.formattedValue;
                        return `${tooltipItem.dataset.label}: ${formattedValue}`;
                      },
                    },
                  },
                },
                scales: {
                  x: {
                    grid: {
                      display: false,
                    },
                  },
                  y: {
                    grid: { display: false },
                    ticks: {
                      format: {
                        style: tickesFormat,
                        ...(tickesFormat === ChartTickesFormatEnum.Currency && {
                          currency: "USD",
                        }),
                      },
                    },
                    max: formatMax,
                  },
                },
                elements: {
                  line: {
                    borderWidth: 6,
                  },
                },
              }}
              data={{
                //@ts-ignore
                datasets,
                labels: chartLabels,
              }}
              key={`${tickesFormat}${datasets.map((d) => d.type).join("-")}`}
            />
          )}
          {props?.loading && (
            <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} spacing={4} height={"100%"}>
              <CircularProgress />
            </Stack>
          )}
        </div>
      </Paper>
    </Stack>
  );
}
